import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import InfoIcon from '../icons/Info'
import { ClassesType } from '../types'
import PuiTooltip, { PuiTooltipProps } from './PuiTooltip'
import TextInteractive, { TextInteractiveProps } from './TextInteractive'

const useStyles = makeStyles(
  theme => ({
    noWrapText: {
      whiteSpace: 'nowrap',
    },
    infoIcon: {
      cursor: 'pointer',
      verticalAlign: 'middle',
      color: theme.colors.brandPrimary,
    },
  }),
  { name: 'TextWithTooltip' },
)

export interface TextWithTooltipProps extends TextInteractiveProps {
  readonly ContainerProps?: Partial<BoxProps>
  readonly Icon?: React.JSXElementConstructor<any>
  readonly IconComponent?: React.ReactElement<any, any>
  readonly IconProps?: object
  readonly TooltipProps?: Partial<PuiTooltipProps>
  readonly allowWrap?: boolean
  readonly className?: string
  readonly classes?: ClassesType<typeof useStyles>
  readonly iconPlacement?: 'left' | 'right'
  readonly noIcon?: boolean
  readonly tooltipDelay?: number
  readonly tooltipText?: React.ReactNode
}

const TextWithTooltip = ({
  ContainerProps,
  allowWrap = false,
  iconPlacement = 'right',
  classes: classesProp,
  className,
  children,
  tooltipText,
  tooltipDelay,
  noIcon,
  Icon = InfoIcon,
  IconComponent: IconComponentProp,
  IconProps,
  TooltipProps,
  ...rest
}: TextWithTooltipProps) => {
  const classes = useStyles({ classes: classesProp })

  const isLeftPlacement = iconPlacement === 'left'
  const isRightPlacement = iconPlacement === 'right'

  const DefaultIconComponent = (
    <Icon
      className={classes.infoIcon}
      sx={{
        mr: isLeftPlacement ? 1 : undefined,
        ml: isRightPlacement ? 1 : undefined,
      }}
      {...IconProps}
    />
  )

  const IconComponent = IconComponentProp || DefaultIconComponent
  const textClassName = classNames(className, {
    [classes.noWrapText]: !allowWrap,
  })

  const tooltip = tooltipText ? (
    <PuiTooltip
      tooltipDelay={tooltipDelay}
      tooltipText={tooltipText}
      {...TooltipProps}
    >
      {noIcon ? (
        <TextInteractive className={textClassName} {...rest}>
          {children}
        </TextInteractive>
      ) : (
        IconComponent
      )}
    </PuiTooltip>
  ) : null

  if (noIcon && tooltipText) {
    return tooltip
  }

  return (
    <Box
      alignItems="center"
      display="flex"
      justifyContent="flex-start"
      {...ContainerProps}
    >
      {isLeftPlacement && tooltip}
      <TextInteractive className={textClassName} {...rest}>
        {children}
      </TextInteractive>
      {isRightPlacement && tooltip}
    </Box>
  )
}

export default TextWithTooltip
